import { IconNames, type IconName } from "@focus/focusUI";
import type { ITableData } from "@focus/focusUI/lib/components/Tables/FocusTable/Entities";
import type { CellContext } from "@tanstack/table-core";
import type { ReactNode } from "react";
import { withTimeSystem } from "../constants/orbitMetadata";
import { FilterType } from "../services/helpers/headers";
import type { InputOrbitRow } from "../services/inputs";
import type { OrbitFileStatus } from "../types/orbits";
import { getComputedInputState, InputStateBadge } from "./inputState";

type OemPrefix = "linkedOEM";

type OemInputPrefix = "parentUserInput" | "opmUserInput";

interface InputsOpsColumnDef {
  title: string;
  hide: boolean;
  field: string;
  key: string;
  filterType: FilterType;
  format?: string;
  filterParams?: {
    buttons: string[];
    closeOnApply: boolean;
  };
  customCellRenderer?: (rowData: CellContext<ITableData, unknown>) => ReactNode;
}

type InputColumnConfig = Omit<InputsOpsColumnDef, "hide" | "key">;

/**
 * Mapping of the status of the orbit file to an icon, color and title, to be used in the inputs tables.
 */
export const ORBIT_STATUS_DISPLAY: Record<
  OrbitFileStatus,
  { icon: IconName; text: string; color: string; title: string }
> = {
  "-": {
    icon: IconNames.MINUS as IconName,
    text: "-",
    color: "#5f6b7c",
    title: "No status",
  },
  PENDING: {
    icon: IconNames.TIME as IconName,
    text: "Pending",
    color: "#738091",
    title: "Pending",
  },
  PROCESSING: {
    icon: IconNames.REFRESH as IconName,
    text: "Processing",
    color: "#2d72d2",
    title: "Processing",
  },
  SCREENING: {
    icon: IconNames.SEARCH as IconName,
    text: "Screening",
    color: "#2d72d2",
    title: "Screening",
  },
  PROCESSED: {
    icon: IconNames.TICK_CIRCLE as IconName,
    text: "Processed",
    color: "#238551",
    title: "Processed",
  },
  ERROR: {
    icon: IconNames.ERROR as IconName,
    text: "Error",
    color: "#c84144",
    title: "Error",
  },
  REJECTED: {
    icon: IconNames.CROSS_CIRCLE as IconName,
    text: "Rejected",
    color: "#c84144",
    title: "Rejected",
  },
};

export const ORBIT_STATUS_FILTER_VALUES = Object.keys(
  ORBIT_STATUS_DISPLAY,
) as OrbitFileStatus[];

const renderStatusCell = (rowData: CellContext<ITableData, unknown>) => {
  const status = (rowData.getValue() as OrbitFileStatus | undefined) ?? "-";
  let statusText = ORBIT_STATUS_DISPLAY[status];
  if (!statusText) {
    statusText = ORBIT_STATUS_DISPLAY["-"];
  }
  return (
    <span
      aria-label={statusText.title}
      className="flex items-center justify-center"
      style={{ color: statusText.color }}
    >
      {statusText.text}
    </span>
  );
};

const renderInputStateCell = (rowData: CellContext<ITableData, unknown>) => (
  <div className="flex items-center justify-center p-1.5">
    <InputStateBadge
      state={getComputedInputState(rowData.row.original as InputOrbitRow)}
    />
  </div>
);

const createInputColumn = (config: InputColumnConfig): InputsOpsColumnDef => {
  return {
    hide: false,
    key: config.field,
    ...config,
  };
};
/**
 *  Helper function to create column definitions for inputs tables, since they share most of the columns and we need to create them dynamically based on the source of the data (OPS or CAT).
 * @param fieldsPrefix
 * @param titlePrefix
 * @returns
 */
const createInputsColumns = (
  fieldsPrefix: "" | `${OemPrefix}.${OemInputPrefix}.`,
  titlePrefix: string,
): InputsOpsColumnDef[] => [
  createInputColumn({
    title: `${titlePrefix}Organization`,
    field: `${fieldsPrefix}organization`,
    filterType: FilterType.TEXT,
  }),
  createInputColumn({
    title: `${titlePrefix}Status`,
    field: `${fieldsPrefix}status`,
    filterType: FilterType.TEXT,
    customCellRenderer: renderStatusCell,
  }),
  createInputColumn({
    title: `${titlePrefix}Filename`,
    field: `${fieldsPrefix}filename`,
    filterType: FilterType.TEXT,
  }),
  createInputColumn({
    title: `${titlePrefix}Uploaded by`,
    field: `${fieldsPrefix}uploadedBy`,
    filterType: FilterType.TEXT,
  }),
  createInputColumn({
    title: withTimeSystem(`${titlePrefix}Created at`),
    field: `${fieldsPrefix}createdAt`,
    filterType: FilterType.DATE,
  }),
  createInputColumn({
    title: withTimeSystem(`${titlePrefix}Updated at`),
    field: `${fieldsPrefix}updatedAt`,
    filterType: FilterType.DATE,
  }),
    createInputColumn({
    title: `${titlePrefix}State`,
    field: `${fieldsPrefix}state`,
    filterType: FilterType.TEXT,
    customCellRenderer: renderInputStateCell,
  }),
];

/**
 * Helper function to create column definitions for the linked OEM in inputs tables, since they share most of the columns and we need to create them dynamically based on the source of the data (OPS or CAT) and also we need to create the actions for the linked OEM (see details and download).
 * @param prefix
 * @param titlePrefix
 * @returns
 */
const createOemColumns = (
  prefix: OemPrefix,
  titlePrefix: string,
): InputsOpsColumnDef[] => [
  createInputColumn({
    title: `${titlePrefix}Cospar Id`,
    field: `${prefix}.spaceObject.cosparId.value`,
    filterType: FilterType.TEXT,
    format: "",
  }),
  createInputColumn({
    title: `${titlePrefix}Norad Id`,
    field: `${prefix}.spaceObject.noradId.value`,
    filterType: FilterType.NUMERICAL,
  }),
  createInputColumn({
    title: `${titlePrefix}Name`,
    field: `${prefix}.spaceObject.name.value`,
    filterType: FilterType.TEXT,
  }),
  createInputColumn({
    title: `${titlePrefix}Space object operator`,
    field: `${prefix}.spaceObject.operator.value`,
    filterType: FilterType.TEXT,
  }),
  createInputColumn({
    title: withTimeSystem(`${titlePrefix}Start date`),
    field: `${prefix}.startDate.value`,
    filterType: FilterType.DATE,
  }),
  createInputColumn({
    title: withTimeSystem(`${titlePrefix}End date`),
    field: `${prefix}.endDate.value`,
    filterType: FilterType.DATE,
  }),
  createInputColumn({
    title: withTimeSystem(`${titlePrefix}Creation date`),
    field: `${prefix}.creationDate.value`,
    filterType: FilterType.DATE,
  }),
  createInputColumn({
    title: `${titlePrefix}Source of information`,
    field: `${prefix}.sourceOfInformation.value`,
    filterType: FilterType.TEXT,
  }),
  createInputColumn({
    title: `${titlePrefix}Category`,
    field: `${prefix}.category.value`,
    filterType: FilterType.TEXT,
  }),
];
/**
 * For inputs table,
 */
export const COLUMNDEFS_INPUTS_OPS: InputsOpsColumnDef[] = [
  ...createInputsColumns("", ""),
  ...createOemColumns("linkedOEM", ""),
];
